// Auth.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Modal,
} from "@mui/material";
import {
  backend,
  clearMessage,
  markPinDisable,
  verifyPin,
} from "../../reducers/userReducer";
import { useDispatch, useSelector } from "react-redux";
import ResetPin from "./resetPin";
import { auth, signInWithPhoneNumber } from "../../service/firebase";
import CroboContainer from "../../components/croboContainer";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/loader";
import OTPInput from "react-otp-input";
import { isWeb, isIos, isAndroid } from "../../utils/platform";
import {
  analytics,
  handlePhoneNumberError,
  initializeRecaptcha,
  triggerHaptic,
} from "../../helper/helper";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import Heading from "../../components/heading";
import {
  NativeBiometric,
  BiometricOptions,
  BiometryType,
} from "capacitor-native-biometric";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import FaceIcon from "@mui/icons-material/Face";

const VerifyPin = ({ sendOtpAndResetPin }) => {
  const [pin, setPin] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(null); // 'face' or 'fingerprint'
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [email, setEmail] = useState("");
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const {
    error: apiError,
    loading,
    pinVerified,
  } = useSelector((state) => state.userAuth);

  useEffect(() => {
    let isMounted = true;
    const getUserInfo = async () => {
      try {
        if (isWeb()) {
          const email = auth?.currentUser?.email;
          const phone = auth?.currentUser?.phoneNumber;
          const isAnonymous = auth?.currentUser?.isAnonymous;
          if (isMounted) {
            setPhone(phone);
            setEmail(email);
            setIsAnonymous(isAnonymous);
            initializeRecaptcha();
          }
        } else {
          const userInfo = await FirebaseAuthentication.getCurrentUser();
          if (isMounted) {
            const email = userInfo.user.email;
            const phone = userInfo.user.phoneNumber;
            const isAnonymous = userInfo.user.isAnonymous;
            setPhone(phone);
            setEmail(email);
            setIsAnonymous(isAnonymous);
          }
        }
      } catch (error) {
        console.error('Error getting user info:', error);
      }
    };

    getUserInfo();

    const checkBiometricAvailability = async () => {
      if (!isWeb()) {
        try {
          const result = await NativeBiometric.isAvailable();
          if (isMounted) {
            setBiometricAvailable(result.isAvailable);

            // Set biometric type based on device capability and platform
            if (result.isAvailable) {
              if (isIos()) {
                // On iOS, check if device supports Face ID
                setBiometricType(
                  result.biometryType === BiometryType.FACE_ID
                    ? BiometryType.FACE_ID
                    : BiometryType.FINGERPRINT
                );
              } else {
                // On Android, assume fingerprint for now as it's most common
                setBiometricType(BiometryType.FINGERPRINT);
              }
            }

            // Check if there's a stored PIN for biometric auth
            const credentials = await NativeBiometric.getCredentials({
              server: "io.ionic.CROBO",
            }).catch(() => null);

            if (credentials) {
              setBiometricAvailable(true);
            }
          }
        } catch (err) {
          if (isMounted) {
            setBiometricAvailable(false);
          }
        }
      }
    };

    checkBiometricAvailability();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let interval;
    if (cooldown) {
      interval = setInterval(() => {
        setCooldownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            setCooldown(false);
            setIsResendDisabled(false);
            return 60;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [cooldown]);

  const handleBiometricAuth = async () => {
    if (!biometricAvailable) return;
    try {
      setLoader(true);
      analytics.track("Biometric Authentication Attempt", {
        type: biometricType,
      });

      const verified = await NativeBiometric.verifyIdentity({
        reason: "Verify your identity",
        title: isIos() ? "Biometric Authentication" : "Verify Identity",
        subtitle: `Use your ${
          biometricType === "face" ? "Face ID" : "fingerprint"
        } to verify your identity`,
        description: "This will allow you to access your account",
        negativeButtonText: "Cancel",
      });

      const credentials = await NativeBiometric.getCredentials({
        server: "io.ionic.CROBO",
      });

      if (credentials && credentials.password) {
        setPin(credentials.password);
        dispatch(verifyPin(credentials.password));
        analytics.track("Biometric Authentication Success", {
          type: biometricType,
        });
      } else {
        setLoader(false);
        setError(
          `${
            biometricType === BiometryType.FACE_ID ? "Face ID" : "Fingerprint"
          } verification failed. Please enter your PIN manually.`
        );
        analytics.track("Biometric Authentication Failed", {
          type: biometricType,
        });
      }
    } catch (error) {
      setLoader(false);
      setError(error.message);
    }
  };

  const handlePin = useCallback(async () => {
    dispatch(verifyPin(pin));

    // Store the PIN for future biometric auth if successful
    if (!isWeb() && biometricAvailable) {
      try {
        await NativeBiometric.setCredentials({
          username: "user",
          password: "1234",
          server: "io.ionic.CROBO",
        });
      } catch (err) {
      }
    }
  }, [pin, dispatch, biometricAvailable]);

  useEffect(() => {
    if (pin.length === 4) {
      setLoader(true);
      handlePin();
    }
  }, [pin, handlePin]);

  useEffect(() => {
    if (pinVerified) {
      const handleSuccess = async () => {
        await triggerHaptic("heavy");
        if (isWeb()) {
          navigate("/transaction");
        } else {
          navigate("/app-home");
        }
      };
      dispatch(clearMessage());
      handleSuccess();
    }
    if (apiError) {
      setLoader(false);
    }
  }, [navigate, pinVerified, dispatch, apiError]);

  const handleResendOtp = () => {
    if (cooldown) return;
    
    analytics.track("OTP Resent", { phone });
    sendOtpAndResetPin(phone ? "phone" : "email");
    setIsResendDisabled(true);
    setCooldown(true);
    setCooldownTime(60);
  };

  return (
    <>
      {loading || loader ? (
        <Loader />
      ) : (
    <Container>
      <Box pt={4} pb={4}>
        <Heading title="Verify Pin" />
      </Box>

      <Box>
        <OTPInput
          value={pin}
          label="PIN"
          inputType="number"
          shouldAutoFocus="true"
          onChange={setPin}
          numInputs={4}
          renderSeparator={<span style={{ margin: 5 }}></span>}
          renderInput={(props) => (
            <input
              {...props}
              style={{
                width: "100%",
                height: "40px",
                fontSize: 18,
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                textAlign: "center",
                fontFamily: "Arial",
              }}
            />
          )}
        />
      </Box>
      <Box display={"flex"} justifyContent={"center"}>
        {(error || apiError) && (
          <Typography textAlign={"center"} sx={{ color: "red" }}>
            {error || apiError}
          </Typography>
        )}
      </Box>
      {false && !isWeb() && biometricAvailable && (
        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              my: 3,
            }}
          >
            <Divider sx={{ flex: 1 }} />
            <Typography sx={{ px: 2, color: "text.secondary" }} variant="body2">
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <Button
            onClick={handleBiometricAuth}
            color="brand"
            size="large"
            fullWidth
            variant="contained"
            disabled={loading || loader}
            startIcon={<FingerprintIcon sx={{ fontSize: 32 }} />}
          >
            Login with{" "}
            {biometricType === BiometryType.FACE_ID ? "Face ID" : "Fingerprint"}
          </Button>
        </Box>
      )}

      <Box paddingY={2}>{loading || loader ? <Loader /> : <></>}</Box>

      <Box sx={{ mt: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          gutterBottom
        >
          Forgot your PIN?
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          {phone && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => sendOtpAndResetPin("phone")}
              sx={{
                textTransform: "none",
              }}
            >
              Reset via Phone
            </Button>
          )}
          {email && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => sendOtpAndResetPin("email")}
              sx={{
                textTransform: "none",
              }}
            >
              Reset via Email
            </Button>
          )}
        </Box>
      </Box>
    </Container>
    )}
    </>
  );
};

const Pin = () => {
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [error, setError] = useState("");
  const [loader, setLoader] = useState(false);
  const [phone, setPhone] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationMethod, setVerificationMethod] = useState("phone");
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  
  useEffect(() => {
     const getUserInfo = async () => {
       if (isWeb()) {
         const email = auth?.currentUser?.email ?? "";
         const phone = auth?.currentUser?.phoneNumber ?? "";
         const isAnonymous = auth?.currentUser?.isAnonymous ?? false;
         setPhone(phone);
         setEmail(email);
         setIsAnonymous(isAnonymous);
         initializeRecaptcha();
       } else {
         const userInfo = await FirebaseAuthentication.getCurrentUser();
         const email = userInfo.user.email ?? "";
         const phone = userInfo.user.phoneNumber ?? "";
         const isAnonymous = userInfo.user.isAnonymous ?? false;
         setPhone(phone);
         setEmail(email);
         setIsAnonymous(isAnonymous);
       }
     };
    getUserInfo();
  }, []);

  const sendOtp = async (method) => {
    try {
      setLoader(true);
      setVerificationMethod(method);

      if (cooldown) {
        setError("Please wait a moment before retrying.");
        return;
      }

      if (method === "email") {
        analytics.track("Email OTP Requested", { email });
        const response = await fetch(`${backend}/auth/pin/reset/send-otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error("Failed to send email OTP");
        }

        setOtpSent(true);
        setLoader(false);
        setIsResendDisabled(true);
        setCooldown(true);
        analytics.track("Email OTP Sent", { email });
      } else {
        analytics.track("OTP Requested", { phone: `+1${phone}` });
        if (isWeb()) {
          // Web implementation
          const appVerifier = window.recaptchaVerifier;
          const confirmationResult = await signInWithPhoneNumber(
            auth,
            `${phone}`,
            appVerifier
          );
          setOtpSent(true);
          setLoader(false);
          setIsResendDisabled(true);
          setCooldown(true);
          analytics.track("OTP Sent", { phone });
          setConfirmationResult(confirmationResult);
        } else {
          // Native implementation
          await FirebaseAuthentication.signInWithPhoneNumber({
            phoneNumber: `${phone}`,
          });

          // Add listener for phoneCodeSent event
          await FirebaseAuthentication.addListener("phoneCodeSent", (event) => {
            setConfirmationResult(event.verificationId);
            setOtpSent(true);
            setLoader(false);
            setIsResendDisabled(true);
            setCooldown(true);
            analytics.track("OTP Sent", { phone });
          });
        }
      }
    } catch (error) {
      if (method === "email") {
        setError("Failed to send email OTP. Please try again.");
        setLoader(false);
        setCooldown(false);
      } else {
        handlePhoneNumberError(error, setError, setLoader, setCooldown, phone);
      }
    }
  };

  const sendOtpAndResetPin = async (method) => {
    await sendOtp(method);
  };

  return (
    <CroboContainer>
      {otpSent ? (
        <ResetPin
          setOtpSent={setOtpSent}
          confirmationResult={confirmationResult}
          setConfirmationResult={setConfirmationResult}
          error={error}
          phone={phone}
          email={email}
          isAnonymous={isAnonymous}
          verificationMethod={verificationMethod}
          cooldown={cooldown}
          setCooldown={setCooldown}
          setError={setError}
          setLoader={setLoader}
          isResendDisabled={isResendDisabled}
          setIsResendDisabled={setIsResendDisabled}
          loader={loader}
        />
      ) : (
        <VerifyPin sendOtpAndResetPin={sendOtpAndResetPin} />
      )}
    </CroboContainer>
  );
};

export default Pin;
